import { Redirect } from 'react-router-dom';

import type { PerformanceProFeatureValue } from '@/constants/PerformanceProFeature';
import type { PermissionValue } from '@/constants/Permission';
import type { ReactNode } from 'react';

import { LockedFeatureState } from '@/components/ProGate/LockedFeatureState';
import { useProGate } from '@/hooks/performance-tier/useProGate';
import { RouteName } from '@/navigation/types';
import { ProGateOutcome } from '@/utils/performanceTier';

interface ProFeatureRouteProps {
    feature: PerformanceProFeatureValue;
    /** The Performance permission the page needs, when the route does not already check it. */
    permission?: PermissionValue;
    children: ReactNode;
}

/**
 * Guards a Performance PRO page by the company's tier, next to (never instead of) `PermissionGuard`.
 *
 * A reader who could buy the feature stays on the page and is offered the upgrade; anyone else is
 * sent to the default route, as a missing permission is. Renders nothing while the answer loads.
 */
export const ProFeatureRoute = ({ feature, permission, children }: ProFeatureRouteProps) => {
    const outcome = useProGate(permission);

    if (outcome === undefined) {
        return null;
    }

    if (outcome === ProGateOutcome.Hide) {
        return <Redirect to={RouteName.Evaluation360} />;
    }

    if (outcome === ProGateOutcome.Lock) {
        return <LockedFeatureState feature={feature} />;
    }

    return <>{children}</>;
};
